import { useAuth } from '@/context/AuthContext';
import { useMenu } from '@/context/MenuContext';

const ALL_ALLOWED = {
  canRead: true,
  canCreate: true,
  canUpdate: true,
  canDelete: true,
  canView: true,
  canExcelUpload: true,
  canExcelDownload: true,
  isAdmin: true,
};

const NONE_ALLOWED = {
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
  canView: false,
  canExcelUpload: false,
  canExcelDownload: false,
  isAdmin: false,
};

/**
 * 전역 권한 관리 훅 (ACL).
 *
 * 인자는 두 가지 형태를 모두 받는다.
 *   - number : menuId (레거시. 기존 페이지들이 사용 중)
 *   - string : menu_code (권장. 예: MENU.CRM_CUSTOMER)
 *
 * menu_code 를 쓰면 고객사별로 메뉴 번호가 달라져도 권한 체크가 깨지지 않는다.
 */
export const usePermission = (menuIdOrCode) => {
  const { myPermissions, user, isPermissionsLoading } = useAuth();
  const { resolveMenuId, isLoading: isMenuLoading } = useMenu();

  const isCode = typeof menuIdOrCode === 'string';
  const menuId = isCode ? resolveMenuId(menuIdOrCode) : menuIdOrCode;

  // menu_code 로 조회하는 경우, 코드 매핑표가 도착하기 전에는 "판정 보류" 상태여야 한다.
  // 그렇지 않으면 로딩 중에 권한 없음으로 오판해 대시보드로 튕긴다.
  const isLoading = isPermissionsLoading || (isCode && isMenuLoading);

  if (!user) return { ...NONE_ALLOWED, isLoading };

  /*
   * 내려받기는 쓰기와 같이 묶어 잠근다.
   *
   * 무료 체험이 끝났는데 결제가 없으면 플랫폼이 access.writable 을 내린다.
   * 그때 등록·수정과 함께 자료를 통째로 퍼 가는 것도 막아야 하므로,
   * 메뉴 권한이 켜져 있어도 writable 이 false 면 내려받기를 내린다.
   *
   * 조회(canRead)는 건드리지 않는다. 보던 자료를 못 보게 하는 것이 아니라
   * 밖으로 빼내는 것만 멈추는 것이다.
   */
  const writable = user.access?.writable !== false;

  // 1. 시스템 관리자는 전체 허용
  const isSuperUser =
    user.roleLevel === 'admin' ||
    user.roles?.includes('Admin') ||
    user.systemUser;

  if (isSuperUser) return { ...ALL_ALLOWED, canExcelDownload: writable, isLoading };

  // 2. 부서/직급 기반 권한 조회
  const found = menuId != null ? myPermissions?.find((p) => p.menuId === menuId) : null;

  if (!found) return { ...NONE_ALLOWED, isLoading };

  return {
    canRead: !!found.canRead,
    canCreate: !!found.canCreate,
    canUpdate: !!found.canUpdate,
    canDelete: !!found.canDelete,
    canView: !!found.canView || !!found.canRead,
    canExcelUpload: !!found.canExcelUpload,
    canExcelDownload: !!found.canExcelDownload && writable,
    isAdmin: false,
    isLoading,
  };
};

/**
 * 정적 컴포넌트에서 훅 없이 즉시 권한을 확인하기 위한 유틸리티.
 * menuId(number)만 지원한다.
 */
export const getStaticPermission = (user, menuId) => {
  if (!user) return { canView: false };

  const isSuperUser =
    user.roleLevel === 'admin' ||
    user.roles?.includes('Admin') ||
    user.systemUser;

  if (isSuperUser) return { ...ALL_ALLOWED };

  const menuPermission = user.permissions?.find((p) => p.menuId === menuId);
  return {
    canRead: !!menuPermission?.canRead,
    canCreate: !!menuPermission?.canCreate,
    canUpdate: !!menuPermission?.canUpdate,
    canDelete: !!menuPermission?.canDelete,
    canView: !!menuPermission?.canView || !!menuPermission?.canRead,
    canExcelUpload: !!menuPermission?.canExcelUpload,
    canExcelDownload: !!menuPermission?.canExcelDownload,
    isAdmin: false,
  };
};
